import { Box, Flex, Icon, Link } from '@chakra-ui/react';
import { useEffect, useMemo, type ReactNode } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kula-ai/components';
import { KText } from '@/ui-library/components/text';
import { formatBytes } from '@/utils/format-bytes';
import { fileTypeIcon } from '@/components/shared/attachments/fileTypeIcon';
import { EllipsisText } from '@/design-system/ellipsis';

/** A file already stored, as the API renders one. */
interface StoredFile {
  id?: number | string;
  url?: string;
  filename?: string;
  byte_size?: number;
  content_type?: string;
}

/**
 * Characters of the stem kept before the ellipsis. A count rather than a
 * measurement, so the cut is the same whatever width the row is given.
 */
const STEM_LENGTH = 13;

interface AttachmentPreviewProps {
  /** A file picked on this device, or one the API returned. */
  file?: File | StoredFile | null;
  opacity?: number | string;
  bg?: string;
  /** Rendered after the file, e.g. a remove button. */
  AfterContent?: ReactNode;
}

/** What the row shows, from either shape of `file`. */
const toPreview = (file?: File | StoredFile | null) => {
  if (file instanceof File) {
    return {
      // A local file has no address until one is made for it.
      url: URL.createObjectURL(file),
      isObjectUrl: true,
      name: file.name,
      size: file.size,
      type: file.type,
    };
  }

  return {
    url: file?.url,
    isObjectUrl: false,
    name: file?.filename,
    size: file?.byte_size,
    type: file?.content_type,
  };
};

/**
 * One attached file: its type tile, its name split so the extension survives the
 * truncation, and its size.
 *
 * Derived from `file` rather than mirrored into state by an effect — the values are
 * a pure function of the prop, and writing them back through `setState` cost a
 * second render on every change (`react-hooks/set-state-in-effect`).
 */
export const AttachmentPreview = ({
  file,
  opacity = 1,
  bg = 'rgba(229, 229, 229, 0.37)',
  AfterContent,
}: AttachmentPreviewProps) => {
  const preview = useMemo(() => toPreview(file), [file]);

  // Only for a URL this component made. Created per `file`, so it has to be released
  // when that changes or the row unmounts, or the blob is held for the life of the
  // page — which the effect this replaced never did.
  useEffect(() => {
    if (!preview.isObjectUrl || !preview.url) return undefined;

    const created = preview.url;
    return () => URL.revokeObjectURL(created);
  }, [preview]);

  if (!preview.url) return null;

  const name = preview.name ?? '';
  const dot = name.lastIndexOf('.');
  const [stem, extension] =
    dot >= 0 ? [name.slice(0, dot), name.slice(dot)] : [name, ''];
  const isClipped = stem.length > STEM_LENGTH;

  // The extension is kept out of the truncation: two files called
  // "Offer_letter_final…" are told apart by ".pdf" against ".docx".
  const fileName = (
    <KText as="span" fontSize="14px" fontWeight="semiBold">
      <EllipsisText
        as="span"
        text={stem}
        length={STEM_LENGTH}
        color="secondary-900"
      />
      <KText as="span" color="secondary-900" fontWeight="semiBold">
        {extension}
      </KText>
    </KText>
  );

  return (
    <Flex
      minW="175px"
      bg={bg}
      borderRadius="6px"
      px="12px"
      py="6px"
      alignItems="center"
      gap="6px"
      className="preview-attachment"
      opacity={opacity}
    >
      <Icon
        as={fileTypeIcon(preview.type)}
        w="42px"
        h="42px"
        borderRadius="6px"
      />
      <Flex flexDirection="column" flex="1">
        <Link
          href={preview.url}
          target="_blank"
          pointerEvents={Number(opacity) === 1 ? 'unset' : 'none'}
          _focus={{ boxShadow: 'none' }}
        >
          {/* Only once the name has actually lost characters — a hover telling
              us "CV.pdf" is called "CV.pdf" is noise on every short name.

              kula `Text truncate tooltip` measures overflow and would be the
              idiomatic choice, but it cuts by width and would take ".pdf" with
              it, and its tooltip would name the stem rather than the file. The
              character cut and the kept extension are the design here, so the
              tooltip is wired by hand. */}
          {isClipped ? (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Box as="span" aria-label={name}>
                    {fileName}
                  </Box>
                </TooltipTrigger>
                <TooltipContent side="top" collisionPadding={12}>
                  {name}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            fileName
          )}
        </Link>
        <KText
          as="span"
          color="secondary-700"
          fontSize="12px"
          fontWeight="semiBold"
        >
          {formatBytes(preview.size)}
        </KText>
      </Flex>
      {AfterContent}
    </Flex>
  );
};
